import type { WeatherAlert } from '../../config/contracts';
import { clsx } from '../../components/clsx';

/**
 * The severe-weather banner. Rendered into `<Card banner>`, so it is
 * full-bleed between the header and the body and costs the forecast strip
 * nothing when there is no alert — the strip is simply absent.
 *
 * Only the worst alert gets a line; the rest collapse into a "+N" count.
 * At 465px wide there is room for exactly one headline, and the spec's
 * requirement is that an alert be PROMINENT, not that all of them be listed.
 */
export function AlertStrip({ alerts }: { alerts: readonly WeatherAlert[] }) {
  const top = alerts[0];
  if (top === undefined) return null;

  const isWarning = top.severity === 'warning';

  return (
    <div
      role="alert"
      data-testid="weather-alert"
      title={top.description ?? top.event}
      className={clsx(
        'flex items-center gap-1.5 border-y px-3 py-1',
        isWarning ? 'border-alert/40 bg-alert/15' : 'border-warn/40 bg-warn/10',
      )}
    >
      <span
        className={clsx(
          'text-xs leading-none',
          isWarning ? 'text-alert motion-safe:animate-pulse' : 'text-warn',
        )}
        aria-hidden
      >
        ⚠
      </span>
      <span
        className={clsx(
          'truncate text-[11px] font-semibold',
          isWarning ? 'text-alert' : 'text-warn',
        )}
      >
        {top.event}
      </span>
      {alerts.length > 1 && (
        <span className="ml-auto shrink-0 text-[10px] font-medium text-ink-subtle">
          +{alerts.length - 1}
        </span>
      )}
    </div>
  );
}
